import { Checkbox, Grid, Modal, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 保存订单商品前，减少数量或被删除的行：用于让操作员逐行决定是否把库存加回去。
 * 仅批发端使用；新增行(无 recId)与增加数量的行不参与。
 */
export type StockRevertRow = {
  recId: number;
  goodsName: string;
  /** 商品属性(SKU规格)：同名商品的不同规格靠它区分 */
  goodsAttrName?: string;
  /** 商品单位 */
  goodsUnit?: string;
  oldNumber: number;
  newNumber: number;
  reduceNumber: number;
  isDelete: boolean;
};

type StockRevertModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  rows: StockRevertRow[];
  /** 确定：回传不加回库存的订单商品recId列表(即未勾选"加回库存"的行) */
  onConfirm: (noRevertStockRecIds: number[]) => void;
};

const StockRevertModal = ({
  visible,
  setVisible,
  rows,
  onConfirm,
}: StockRevertModalProps) => {
  const screens = Grid.useBreakpoint();
  const { t } = useTranslation();
  // 勾选表示加回库存。默认全部加回：误勾的后果是库存偏多(可盘点纠正)，而不是库存凭空消失
  const [revertRecIds, setRevertRecIds] = useState<number[]>([]);

  useEffect(() => {
    if (visible) {
      setRevertRecIds(rows.map((row) => row.recId));
    }
  }, [visible, rows]);

  const columns: ColumnsType<StockRevertRow> = [
    {
      title: t('order.orderDetail.revertStock.goodsName'),
      width: 260,
      render: (record: StockRevertRow) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{record.goodsName}</Typography.Text>
          {record.goodsAttrName && (
            <Typography.Text type="secondary">
              {record.goodsAttrName}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: t('goods.goodsListColumns.goodsUnit'),
      width: 120,
      dataIndex: 'goodsUnit',
    },
    {
      title: t('order.orderDetail.revertStock.change'),
      width: 90,
      render: (record: StockRevertRow) =>
        record.isDelete
          ? t('order.orderDetail.revertStock.deleted')
          : `${record.oldNumber} → ${record.newNumber}`,
    },
    {
      title: t('order.orderDetail.revertStock.reduceNumber'),
      width: 80,
      dataIndex: 'reduceNumber',
    },
    {
      title: (
        <Checkbox
          checked={rows.length > 0 && revertRecIds.length === rows.length}
          indeterminate={
            revertRecIds.length > 0 && revertRecIds.length < rows.length
          }
          onChange={(e) =>
            setRevertRecIds(
              e.target.checked ? rows.map((row) => row.recId) : []
            )
          }
        >
          {t('order.orderDetail.revertStock.revert')}
        </Checkbox>
      ),
      width: 110,
      render: (record: StockRevertRow) => (
        <Checkbox
          checked={revertRecIds.includes(record.recId)}
          onChange={(e) =>
            setRevertRecIds((prev) =>
              e.target.checked
                ? prev.concat(record.recId)
                : prev.filter((recId) => recId !== record.recId)
            )
          }
        />
      ),
    },
  ];

  return (
    <Modal
      visible={visible}
      title={t('order.orderDetail.revertStock.title')}
      okText={t('general.ok')}
      cancelText={t('general.cancel')}
      maskClosable={false}
      width={screens.md ? 760 : undefined}
      onCancel={() => setVisible(false)}
      onOk={() => {
        const noRevertStockRecIds = rows
          .filter((row) => !revertRecIds.includes(row.recId))
          .map((row) => row.recId);
        setVisible(false);
        onConfirm(noRevertStockRecIds);
      }}
    >
      <Typography.Paragraph>
        {t('order.orderDetail.revertStock.description')}
      </Typography.Paragraph>
      <Table
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row.recId}
        pagination={false}
        size="small"
        scroll={{ y: 300, x: 660 }}
      />
    </Modal>
  );
};

export default StockRevertModal;
